
import React, { useState, useEffect, useMemo, useCallback, lazy, Suspense } from 'react';
import {
  X, Plus, CheckSquare, Footprints, MapPin, Minus,
  Pentagon, FolderOpen, Trash2, FolderInput, Download, Upload, Lock, Layers, ClipboardList, Loader2,
  Map as MapIcon, Users, Truck, Locate, ChevronDown, ChevronRight, Pencil, Check
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { doc, getDoc, collection, query, where, getDocs, orderBy, limit } from 'firebase/firestore';
import { db, suggestDeletion } from '../../services/firebaseService';
import {
  UserContent, UserContentFolder, ContentType,
  WaypointContent, TrackContent, LineContent, AreaContent,
  CollaborativeMap, AppUser, Vehicle
} from '../../types';
import {
  deleteContent, moveToFolder, createFolder, deleteFolder, updateFolder,
  exportContentAsGpx, downloadGpxFile, publishWaypointAsSpot
} from '../../services/myContentService';
import { useToast } from '../../contexts/ToastContext';
import { Globe } from 'lucide-react';
import MyContentItem from './MyContentItem';
import MyContentEmptyState from './MyContentEmptyState';
import MyContentImportDialog from './MyContentImportDialog';
import AuthNudge from '../common/AuthNudge';
import CollaborativeMapModal from '../collaborative/CollaborativeMapModal';
import { loadPackingLists, savePackingLists } from './packingStorage';
import { hasUnseenCollabContent, hasUnreadCollabChat, markCollabContentSeen } from '../../services/collaborativeMapsService';
import type { PackingList } from './packingTypes';
import type { TabKey } from '../../types/sidebar';

const PackingListEmbed = lazy(() => import('./PackingListEmbed'));

const TAB_ICONS: Record<TabKey, React.ReactNode> = {
  track: <Footprints size={12} />,
  waypoint: <MapPin size={12} />,
  line: <Minus size={12} strokeWidth={3} />,
  area: <Pentagon size={12} />,
  folders: <FolderOpen size={12} />,
  packing: <ClipboardList size={12} />,
};

const TAB_KEYS: TabKey[] = ['folders', 'packing', 'track', 'waypoint', 'line', 'area'];
const TAB_I18N: Record<TabKey, string> = {
  track: 'myContent.tabs.tracks',
  waypoint: 'myContent.tabs.waypoints',
  line: 'myContent.tabs.lines',
  area: 'myContent.tabs.areas',
  folders: 'myContent.tabs.folders',
  packing: 'myContent.tabs.packing',
};

/**
 * Halv utstrekning (grader) vi minst flyr til rundt et punkt. Punkt-innhold
 * (waypoints, besøk) lagres med en null-stor bounds — latMin===latMax. Sendt
 * rett til flyToBounds klemmer Leaflet zoomen til maxZoom, så brukeren lander
 * på et fullstendig innzoomet kart uten kontekst. ±0,005° ≈ 550 m, samme
 * utvidelse som «vis mappe på kart» og samarbeidskart-visningen bruker.
 */
const MIN_FLY_HALF_SPAN = 0.005;

/** Bygg en flybar bounds for ett innholdselement, eller null om det mangler posisjon. */
export function flyBoundsForItem(item: UserContent): { latMin: number; latMax: number; lonMin: number; lonMax: number } | null {
  let base = item.bounds;
  if (!base && item.type === 'waypoint') {
    const wp = item as WaypointContent;
    if (!Number.isFinite(wp.latitude) || !Number.isFinite(wp.longitude)) return null;
    base = { latMin: wp.latitude, latMax: wp.latitude, lonMin: wp.longitude, lonMax: wp.longitude };
  }
  if (!base) return null;
  let { latMin, latMax, lonMin, lonMax } = base;
  if (![latMin, latMax, lonMin, lonMax].every(Number.isFinite)) return null;
  if (latMax - latMin < MIN_FLY_HALF_SPAN * 2) {
    const c = (latMin + latMax) / 2;
    latMin = c - MIN_FLY_HALF_SPAN;
    latMax = c + MIN_FLY_HALF_SPAN;
  }
  if (lonMax - lonMin < MIN_FLY_HALF_SPAN * 2) {
    const c = (lonMin + lonMax) / 2;
    lonMin = c - MIN_FLY_HALF_SPAN;
    lonMax = c + MIN_FLY_HALF_SPAN;
  }
  return { latMin, latMax, lonMin, lonMax };
}

export interface MyContentInnerProps {
  content: UserContent[];
  folders: UserContentFolder[];
  isPaidUser: boolean;
  userId?: string;
  initialTab?: TabKey;
  isVisible: boolean;
  onClose: () => void;
  onFlyToContent?: (bounds: { latMin: number; latMax: number; lonMin: number; lonMax: number }) => void;
  /** Highlight a specific set of content items on the map (e.g. when "show folder on map" is tapped). */
  onShowContentOnMap?: (items: UserContent[]) => void;
  onStartDrawing?: (mode: 'line' | 'area') => void;
  onViewTrack?: (track: TrackContent) => void;
  onShowNudge?: (key: 'drawing_tools' | 'content_folders') => void;
  collaborativeMaps?: CollaborativeMap[];
  currentUser?: AppUser | null;
  onOpenCollabMap?: (mapId: string) => void;
  onShowCollabMapOnMap?: (mapId: string) => void;
  onCreateCollabMap?: () => void;
  spotPhotos?: Map<string, string>;
  reviewedSpotIds?: Set<string>;
  showCloseButton?: boolean;
  /** Spots for inline collab map rendering */
  allSpots?: import('../../types').CampingSpot[];
  /** Routes for inline collab map rendering */
  allRoutes?: import('../../types').GpxRoute[];
  /** Called when a spot is selected from an inline collab map */
  onSelectSpotFromCollabMap?: (spot: import('../../types').CampingSpot, fromMapId: string) => void;
  /** Called when a route is selected from an inline collab map */
  onSelectRouteFromCollabMap?: (routeId: string, fromMapId: string) => void;
  /** Called when waypoints are published as Fricamp spots — for optimistic local state update */
  onWaypointsPublished?: (spots: import('../../types').CampingSpot[]) => void;
}

const MyContentInner: React.FC<MyContentInnerProps> = ({
  content, folders, isPaidUser, userId,
  initialTab = 'folders',
  isVisible, onClose,
  onFlyToContent, onShowContentOnMap, onStartDrawing, onViewTrack, onShowNudge,
  collaborativeMaps = [], currentUser, onOpenCollabMap, onShowCollabMapOnMap, onCreateCollabMap, spotPhotos, reviewedSpotIds,
  showCloseButton = true,
  allSpots, allRoutes, onSelectSpotFromCollabMap, onSelectRouteFromCollabMap,
  onWaypointsPublished,
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabKey>(initialTab);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [editingFolderId, setEditingFolderId] = useState<string | null>(null);
  const [editingFolderName, setEditingFolderName] = useState('');
  const [showMoveDialog, setShowMoveDialog] = useState<string | null>(null);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [waypointFilter, setWaypointFilter] = useState<'all' | 'visits' | 'custom'>('all');
  const [vehicleBannerUrl, setVehicleBannerUrl] = useState<string | null>(null);
  const [expandedFolderIds, setExpandedFolderIds] = useState<Set<string>>(new Set());
  const [inlineCollabMapId, setInlineCollabMapId] = useState<string | null>(null);
  const [packingLists, setPackingLists] = useState<PackingList[]>(() => loadPackingLists());
  const [selectedPackingListId, setSelectedPackingListId] = useState<string | null>(null);
  const [openingTrackId, setOpeningTrackId] = useState<string | null>(null);
  const [publishingAll, setPublishingAll] = useState(false);
  const toast = useToast();

  // Private waypoints (uten linkedSpotId, ikke visit-bridges) — kandidater for publisering.
  const unsharedWaypoints = useMemo(() => {
    return content.filter((c): c is WaypointContent =>
      c.type === 'waypoint' &&
      !c.id.startsWith('visit_') &&
      !(c as WaypointContent).linkedSpotId
    );
  }, [content]);

  const handlePublishAllUnshared = async () => {
    if (!currentUser || unsharedWaypoints.length === 0 || publishingAll) return;
    if (!window.confirm(t(
      'myContent.publish_all_confirm',
      'Publiser {{count}} private punkter som Fricamp-plasser? Andre brukere vil se dem på kartet (med 150m-regelen som gjelder for alle).',
      { count: unsharedWaypoints.length }
    ))) return;

    setPublishingAll(true);
    let success = 0;
    let failed = 0;
    const newSpots: import('../../types').CampingSpot[] = [];
    for (const wp of unsharedWaypoints) {
      try {
        const result = await publishWaypointAsSpot(wp, currentUser);
        if (result.spot) newSpots.push(result.spot as import('../../types').CampingSpot);
        success++;
      } catch (err) {
        console.warn('[publishAll] failed for', wp.id, err);
        failed++;
      }
    }
    if (newSpots.length > 0) onWaypointsPublished?.(newSpots);
    setPublishingAll(false);
    if (failed === 0) {
      toast(t('myContent.publish_all_success', '{{count}} punkter publisert', { count: success }), 'success');
    } else {
      toast(t('myContent.publish_all_partial', '{{success}} publisert, {{failed}} feilet', { success, failed }), 'warning');
    }
  };

  const refreshPackingLists = useCallback(() => {
    setPackingLists(loadPackingLists());
  }, []);

  // Reset tab when initialTab changes (e.g. opened from different context)
  useEffect(() => {
    setActiveTab(initialTab);
  }, [initialTab]);

  // Fetch active vehicle photo for banner
  useEffect(() => {
    if (!isVisible || !currentUser?.uid) {
      setVehicleBannerUrl(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        let v: Vehicle | undefined;
        const uid = currentUser.uid;

        if (currentUser.activeVehicleId) {
          const vDoc = await getDoc(doc(db, 'users', uid, 'vehicles', currentUser.activeVehicleId));
          v = vDoc.data() as Vehicle | undefined;
        }

        if (!v) {
          const col = collection(db, 'users', uid, 'vehicles');
          const defSnap = await getDocs(query(col, where('is_default', '==', true), limit(1)));
          if (defSnap.docs.length > 0) {
            v = defSnap.docs[0].data() as Vehicle;
          } else {
            const anySnap = await getDocs(query(col, orderBy('created_at', 'asc'), limit(1)));
            if (anySnap.docs.length > 0) {
              v = anySnap.docs[0].data() as Vehicle;
            }
          }
        }

        if (cancelled) return;
        setVehicleBannerUrl(
          v?.photo_urls?.[0] || v?.photo_url || v?.tow_vehicle?.photo_url || null
        );
      } catch {
        if (!cancelled) setVehicleBannerUrl(null);
      }
    })();
    return () => { cancelled = true; };
  }, [isVisible, currentUser?.uid, currentUser?.activeVehicleId]);

  // Filter content by active tab
  const filteredContent = useMemo(() => {
    if (activeTab === 'folders') return content;
    let items = content.filter(c => c.type === activeTab);
    if (activeTab === 'waypoint' && waypointFilter !== 'all') {
      if (waypointFilter === 'visits') {
        items = items.filter(c => c.id.startsWith('visit_'));
      } else {
        items = items.filter(c => !c.id.startsWith('visit_'));
      }
    }
    return items;
  }, [content, activeTab, waypointFilter]);

  const handleToggleSelect = useCallback((id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  /**
   * Bare admin kan faktisk fjerne en delt plass fra kartet — markSpotAsInvalid
   * returnerer tidlig for alle andre. Å spørre en vanlig bruker «skal den
   * slettes for alle?» ville derfor vært et løfte vi ikke kan holde. De får i
   * stedet plassen meldt inn til gjennomgang.
   */
  const isAdmin = !!currentUser?.isAdmin;

  const reportSharedSpots = useCallback(async (items: WaypointContent[]) => {
    if (!userId || items.length === 0) return 0;
    let reported = 0;
    for (const item of items) {
      try {
        await suggestDeletion(
          item.linkedSpotId!,
          userId,
          'Eier slettet sitt eget punkt i Mitt innhold',
          currentUser?.displayName ?? null
        );
        reported++;
      } catch (err) {
        console.warn('[MyContent] could not report shared spot for review:', err);
      }
    }
    return reported;
  }, [userId, currentUser?.displayName]);

  const handleDeleteItem = async (id: string) => {
    const item = content.find(c => c.id === id);
    if (!item) return;

    if (!window.confirm(t('myContent.delete_item_confirm', 'Slette «{{name}}»?', { name: item.name }))) return;

    const linked = item.type === 'waypoint' ? (item as WaypointContent) : undefined;
    const hasLinkedSpot = !!linked?.linkedSpotId;
    const cascade = hasLinkedSpot && isAdmin && window.confirm(
      t('myContent.cascade_delete_confirm', 'Dette punktet er også delt med fellesskapet. Slette den delte plassen fra kartet for alle andre også?')
    );

    try {
      await deleteContent(id, { alsoDeleteLinkedSpot: cascade });
      setSelectedIds(prev => { const n = new Set(prev); n.delete(id); return n; });

      if (hasLinkedSpot && !isAdmin) {
        const reported = await reportSharedSpots([linked!]);
        toast(
          reported > 0
            ? t('myContent.shared_spot_reported', 'Punktet er slettet. Den delte plassen er meldt til gjennomgang.')
            : t('myContent.item_deleted', 'Slettet'),
          'success'
        );
      } else {
        toast(t('myContent.item_deleted', 'Slettet'), 'success');
      }
    } catch (err) {
      console.error('[MyContent] delete failed:', err);
      toast(t('common.error_generic', 'Noe gikk galt'), 'error');
    }
  };

  const handleBulkDelete = async () => {
    // Besøk er syntetiske rader uten dokument — de kan velges i velgemodus,
    // men det finnes ingenting å slette.
    const deletableIds = Array.from(selectedIds).filter(id => !id.startsWith('visit_'));
    const skippedVisits = selectedIds.size - deletableIds.length;

    if (deletableIds.length === 0) {
      toast(t('myContent.bulk_delete_only_visits', 'Besøk kan ikke slettes herfra'), 'error');
      return;
    }

    if (!window.confirm(
      t('myContent.bulk_delete_confirm', 'Slette {{count}} elementer?', { count: deletableIds.length })
    )) return;

    const linkedItems = deletableIds
      .map(id => content.find(c => c.id === id))
      .filter((c): c is WaypointContent => c?.type === 'waypoint' && !!c.linkedSpotId);
    const cascade = linkedItems.length > 0 && isAdmin && window.confirm(
      t('myContent.bulk_cascade_delete_confirm',
        '{{count}} av punktene er delt med fellesskapet. Slette de delte plassene fra kartet for alle også?',
        { count: linkedItems.length })
    );

    let deleted = 0;
    let failed = 0;
    for (const id of deletableIds) {
      const item = content.find(c => c.id === id);
      const isLinkedWaypoint = item?.type === 'waypoint' && !!(item as WaypointContent).linkedSpotId;
      try {
        await deleteContent(id, { alsoDeleteLinkedSpot: cascade && isLinkedWaypoint });
        deleted++;
      } catch (err) {
        console.error('[MyContent] bulk delete failed for', id, err);
        failed++;
      }
    }

    if (linkedItems.length > 0 && !isAdmin) await reportSharedSpots(linkedItems);

    setSelectedIds(new Set());
    setIsSelectMode(false);

    if (failed > 0) {
      toast(t('myContent.bulk_delete_partial', '{{deleted}} slettet, {{failed}} feilet', { deleted, failed }), 'error');
    } else {
      toast(
        skippedVisits > 0
          ? t('myContent.bulk_delete_skipped_visits', '{{count}} slettet. Besøk ble hoppet over.', { count: deleted })
          : t('myContent.bulk_deleted', '{{count}} slettet', { count: deleted }),
        'success'
      );
    }
  };

  const handleExportSelected = () => {
    const items = content.filter(c => selectedIds.has(c.id));
    if (items.length === 0) return;
    const gpx = exportContentAsGpx(items);
    downloadGpxFile(gpx, `fricamp-${items.length}-elementer.gpx`);
  };

  const handleExportAll = () => {
    const items = activeTab === 'folders' ? content : content.filter(c => c.type === activeTab);
    if (items.length === 0) return;
    const gpx = exportContentAsGpx(items);
    downloadGpxFile(gpx, `fricamp-eksport.gpx`);
  };

  /**
   * Felles for både bulk-flytting og flytting av ett element. moveToFolder
   * siler ut elementer som ikke kan ligge i mapper (legacy-broer og besøk) og
   * rapporterer dem tilbake, så brukeren får vite det i stedet for at
   * dialogen bare blir stående.
   */
  const runMoveToFolder = async (ids: string[], folderId: string | null) => {
    try {
      const { moved, skipped } = await moveToFolder(ids, folderId);
      if (skipped.length > 0) {
        toast(
          moved > 0
            ? t('myContent.move_partial', '{{moved}} flyttet. {{skipped}} kan ikke legges i mapper.', { moved, skipped: skipped.length })
            : t('myContent.move_not_supported', 'Dette elementet kan ikke legges i mapper'),
          'error'
        );
      } else {
        toast(t('myContent.moved_to_folder', 'Flyttet'), 'success');
      }
    } catch (err) {
      console.error('[MyContent] move to folder failed:', err);
      toast(t('common.error_generic', 'Noe gikk galt'), 'error');
    } finally {
      setShowMoveDialog(null);
    }
  };

  const handleBulkMove = async (folderId: string | null) => {
    await runMoveToFolder(Array.from(selectedIds), folderId);
    setSelectedIds(new Set());
    setIsSelectMode(false);
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !userId) return;
    await createFolder(userId, newFolderName.trim());
    setNewFolderName('');
    setShowNewFolder(false);
  };

  const handleStartRename = (folder: UserContentFolder) => {
    setEditingFolderId(folder.id);
    setEditingFolderName(folder.name);
  };

  const handleSaveRename = async () => {
    if (!editingFolderId) return;
    const name = editingFolderName.trim();
    if (!name) { setEditingFolderId(null); return; }
    const folder = folders.find(f => f.id === editingFolderId);
    if (folder && folder.name !== name) {
      try {
        await updateFolder(editingFolderId, { name });
        toast(t('myContent.folder_renamed', 'Mappe omdøpt'), 'success');
      } catch {
        toast(t('common.error_generic', 'Noe gikk galt'), 'error');
      }
    }
    setEditingFolderId(null);
    setEditingFolderName('');
  };

  const handleDeleteFolder = async (folder: UserContentFolder) => {
    const itemsInFolder = content.filter(c => c.folderId === folder.id);
    const packingInFolder = packingLists.filter(pl => pl.folderId === folder.id);
    const childCount = itemsInFolder.length + packingInFolder.length;
    const message = childCount > 0
      ? t('myContent.delete_folder_confirm_items',
          'Slette mappen «{{name}}»? Innholdet ({{count}}) flyttes ut av mappen, men slettes ikke.',
          { name: folder.name, count: childCount })
      : t('myContent.delete_folder_confirm',
          'Slette mappen «{{name}}»?', { name: folder.name });
    if (!window.confirm(message)) return;
    try {
      // Flytt innhold ut av mappen før sletting, så det ikke blir foreldreløst.
      if (itemsInFolder.length > 0) {
        await moveToFolder(itemsInFolder.map(i => i.id), null);
      }
      if (packingInFolder.length > 0) {
        const updated = packingLists.map(pl =>
          pl.folderId === folder.id ? { ...pl, folderId: null } : pl
        );
        savePackingLists(updated);
        setPackingLists(updated);
      }
      await deleteFolder(folder.id);
      setExpandedFolderIds(prev => { const n = new Set(prev); n.delete(folder.id); return n; });
      toast(t('myContent.folder_deleted', 'Mappe slettet'), 'success');
    } catch {
      toast(t('common.error_generic', 'Noe gikk galt'), 'error');
    }
  };

  const handleTapItem = async (item: UserContent) => {
    if (item.type === 'track' && onViewTrack) {
      setOpeningTrackId(item.id);
      try {
        await onViewTrack(item as TrackContent);
      } finally {
        setOpeningTrackId(null);
      }
      return;
    }
    if (!onFlyToContent) return;
    const bounds = flyBoundsForItem(item);
    if (!bounds) return;
    onFlyToContent(bounds);
    onClose();
  };

  const handleShowFolderOnMap = useCallback((folderId: string) => {
    const items = content.filter(c => c.folderId === folderId);
    if (!items.length) return;
    let latMin = Infinity, latMax = -Infinity, lonMin = Infinity, lonMax = -Infinity;
    for (const item of items) {
      if (item.bounds) {
        latMin = Math.min(latMin, item.bounds.latMin);
        latMax = Math.max(latMax, item.bounds.latMax);
        lonMin = Math.min(lonMin, item.bounds.lonMin);
        lonMax = Math.max(lonMax, item.bounds.lonMax);
      } else if (item.type === 'waypoint') {
        const wp = item as WaypointContent;
        latMin = Math.min(latMin, wp.latitude);
        latMax = Math.max(latMax, wp.latitude);
        lonMin = Math.min(lonMin, wp.longitude);
        lonMax = Math.max(lonMax, wp.longitude);
      }
    }
    if (latMin === Infinity) return;
    if (latMin === latMax) { latMin -= 0.005; latMax += 0.005; }
    if (lonMin === lonMax) { lonMin -= 0.005; lonMax += 0.005; }
    onShowContentOnMap?.(items);
    onFlyToContent?.({ latMin, latMax, lonMin, lonMax });
    onClose();
  }, [content, onFlyToContent, onShowContentOnMap, onClose]);

  const handleStartDraw = (mode: 'line' | 'area') => {
    if (!isPaidUser) {
      onShowNudge?.('drawing_tools');
      return;
    }
    onStartDrawing?.(mode);
    onClose();
  };

  // Inline collaborative map view — replaces the normal MyContent UI
  if (inlineCollabMapId && currentUser) {
    return (
      <CollaborativeMapModal
        mapId={inlineCollabMapId}
        maps={collaborativeMaps}
        spots={allSpots || []}
        routes={allRoutes}
        currentUser={currentUser}
        isPaidUser={isPaidUser}
        inline={true}
        onClose={() => setInlineCollabMapId(null)}
        onSelectSpot={onSelectSpotFromCollabMap}
        onSelectRoute={onSelectRouteFromCollabMap}
      />
    );
  }
  return (
    <div className="h-full flex flex-col overflow-hidden">
      {!currentUser ? (
        <AuthNudge
          title={t('auth.mycontent_title', 'Ditt innhold')}
          description={t('auth.mycontent_desc', 'Logg inn for å lage egne ruter, områder og pakkelister som synkroniseres på tvers av enhetene dine.')}
          onOpenAuth={onClose} // Typically clicking AuthNudge would trigger some global auth modal
        />
      ) : (
        <>
          {/* Vehicle banner */}
          <div className="relative h-[80px] flex-shrink-0 overflow-hidden">
            {vehicleBannerUrl ? (
              <img
                src={vehicleBannerUrl}
                alt=""
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full bg-gradient-to-r from-emerald-900 via-stone-800 to-emerald-950 flex items-center justify-center gap-3">
                <Truck size={28} className="text-emerald-400/60" />
                <div className="flex flex-col">
                  <span className="text-tiny font-black text-emerald-400/80 tracking-wider uppercase">{t("auto.mycontent.mycontentinner.mitt_innhold", "Mitt innhold")}</span>
                  <span className="text-4xs text-emerald-500/50 font-medium">{t("auto.mycontent.mycontentinner.legg_til_kjoretoybilde_i_garas", "Legg til kjøretøybilde i garasjen")}</span>
                </div>
              </div>
            )}
            <div className="absolute inset-0 bg-gradient-to-t from-[var(--bg-surface)] via-transparent to-transparent" />
          </div>

          {/* Header */}
          <div className="flex items-center justify-between px-5 pt-2 pb-3 flex-shrink-0">
            <h2 className="text-[13px] font-black text-[var(--text-primary)]">{t('myContent.title')}</h2>
            <div className="flex items-center gap-1">
              <button
                onClick={() => { setIsSelectMode(!isSelectMode); setSelectedIds(new Set()); }}
                className={`p-2 rounded-xl transition-colors ${isSelectMode ? 'bg-emerald-500/20 text-emerald-400' : 'text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]'}`}
              >
                <CheckSquare size={15} />
              </button>
              {showCloseButton && (
                <button onClick={onClose} className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-xl hover:bg-[var(--bg-surface-alt)]">
                  <X size={16} />
                </button>
              )}
            </div>
          </div>

          {/* Tab strip */}
          <div className="flex gap-0.5 px-4 pb-3 flex-shrink-0 overflow-x-auto no-scrollbar">
            {TAB_KEYS.map(key => (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 px-1.5 py-2 min-h-[44px] rounded-lg text-3xs font-bold whitespace-nowrap transition-colors ${
                  activeTab === key
                    ? 'bg-emerald-500/20 text-emerald-400'
                    : 'text-[var(--text-muted)] hover:text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]'
                }`}
              >
                {TAB_ICONS[key]}
                <span>{t(TAB_I18N[key])}</span>
              </button>
            ))}
          </div>

          {/* Add / Draw buttons (hidden on packing tab) */}
          {activeTab !== 'packing' && <div className="flex gap-2 px-5 pb-3 flex-shrink-0">
            <div className="relative flex-1">
              <button
                onClick={() => setShowAddMenu(!showAddMenu)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-2xl border bg-emerald-500/10 border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/20 active:scale-95 transition-all text-tiny font-bold"
              >
                <Plus size={14} /> {t('myContent.add')}
              </button>
              {showAddMenu && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-[var(--bg-input)] rounded-xl border border-[var(--border-secondary)] shadow-xl overflow-hidden z-20">
                  <button
                    onClick={() => { setShowAddMenu(false); handleStartDraw('line'); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-tiny text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]"
                  >
                    <Minus size={13} strokeWidth={3} className="text-blue-400" />
                    {t('myContent.draw_line')}
                    {!isPaidUser && <Lock size={11} className="text-[var(--text-muted)] ml-auto" />}
                  </button>
                  <button
                    onClick={() => { setShowAddMenu(false); handleStartDraw('area'); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-tiny text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]"
                  >
                    <Pentagon size={13} className="text-violet-400" />
                    {t('myContent.draw_area')}
                    {!isPaidUser && <Lock size={11} className="text-[var(--text-muted)] ml-auto" />}
                  </button>
                  <div className="border-t border-[var(--border-primary)]" />
                  <button
                    onClick={() => { setShowAddMenu(false); setShowImport(true); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-tiny text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]"
                  >
                    <Upload size={13} className="text-amber-400" />
                    {t('myContent.import_gpx')}
                  </button>
                  <button
                    onClick={() => { setShowAddMenu(false); handleExportAll(); }}
                    className="w-full flex items-center gap-2 px-4 py-3 text-tiny text-[var(--text-primary)] hover:bg-[var(--bg-surface-alt)]"
                  >
                    <Download size={13} className="text-sky-400" />
                    {t('myContent.export_gpx')}
                  </button>
                </div>
              )}
            </div>
          </div>}

          {/* Waypoint sub-filter chips */}
          {activeTab === 'waypoint' && (
            <div className="flex gap-1.5 px-5 pb-2 flex-shrink-0">
              {([
                { key: 'all' as const, label: t('myContent.filter_all') },
                { key: 'visits' as const, label: t('myContent.filter_visited') },
                { key: 'custom' as const, label: t('myContent.filter_custom') },
              ]).map(f => (
                <button
                  key={f.key}
                  onClick={() => setWaypointFilter(f.key)}
                  className={`px-2.5 py-1 rounded-lg text-3xs font-bold transition-colors ${
                    waypointFilter === f.key
                      ? 'bg-emerald-500/20 text-emerald-400'
                      : 'bg-[var(--bg-input)] text-[var(--text-muted)] hover:text-[var(--text-secondary)]'
                  }`}
                >
                  {f.label}
                </button>
              ))}
            </div>
          )}

          {/* Bulk publish banner — only on waypoint tab when unshared waypoints exist */}
          {activeTab === 'waypoint' && unsharedWaypoints.length > 0 && currentUser && (
            <div className="mx-5 mb-2 p-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/5 flex-shrink-0">
              <div className="flex items-start gap-2">
                <Globe size={14} className="text-emerald-400 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-tiny font-bold text-[var(--text-primary)]">
                    {t('myContent.publish_all_title', '{{count}} private punkter er ikke delt', { count: unsharedWaypoints.length })}
                  </p>
                  <p className="text-3xs text-[var(--text-muted)] leading-snug mt-0.5">
                    {t('myContent.publish_all_subtitle', 'Publiser dem som Fricamp-plasser slik at andre ser dem på kartet.')}
                  </p>
                </div>
                <button
                  onClick={handlePublishAllUnshared}
                  disabled={publishingAll}
                  className="shrink-0 px-3 py-1.5 rounded-lg bg-emerald-500 text-white text-2xs font-black uppercase tracking-wide hover:bg-emerald-600 active:scale-95 transition-all disabled:opacity-50 flex items-center gap-1"
                >
                  {publishingAll ? <Loader2 size={10} className="animate-spin" /> : <Globe size={10} />}
                  {publishingAll
                    ? t('myContent.publishing', 'Publiserer ...')
                    : t('myContent.publish_all_action', 'Publiser alle')}
                </button>
              </div>
            </div>
          )}

          {/* Content list */}
          <div className="flex-1 overflow-y-auto px-5 pb-6 space-y-2" style={{ overscrollBehavior: 'contain' }}>
            {activeTab === 'packing' ? (
              <Suspense fallback={<div className="flex items-center justify-center py-12"><Loader2 size={20} className="animate-spin text-[var(--text-muted)]" /></div>}>
                <PackingListEmbed
                  folders={isPaidUser ? folders : undefined}
                  initialListId={selectedPackingListId}
                  onListsChanged={refreshPackingLists}
                />
              </Suspense>
            ) : activeTab === 'folders' ? (
              <div className="space-y-2">
                {isPaidUser ? (
                  <>
                    {showNewFolder ? (
                      <div className="flex items-center gap-2 p-3 rounded-2xl bg-[var(--bg-input)]/60 border border-[var(--border-primary)]">
                        <input
                          type="text"
                          value={newFolderName}
                          onChange={e => setNewFolderName(e.target.value)}
                          placeholder={t('myContent.folder_name_placeholder')}
                          className="flex-1 bg-transparent text-[12px] text-[var(--text-primary)] placeholder-[var(--text-muted)] outline-none"
                          autoFocus
                          onKeyDown={e => e.key === 'Enter' && handleCreateFolder()}
                        />
                        <button
                          onClick={handleCreateFolder}
                          className="px-3 py-1 bg-emerald-500 text-white rounded-lg text-2xs font-bold"
                        >
                          {t('myContent.create')}
                        </button>
                        <button
                          onClick={() => { setShowNewFolder(false); setNewFolderName(''); }}
                          className="p-1 text-[var(--text-muted)] hover:text-[var(--text-secondary)]"
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowNewFolder(true)}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-[var(--border-secondary)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-secondary)] transition-colors text-tiny font-bold"
                      >
                        <Plus size={14} /> {t('myContent.new_folder')}
                      </button>
                    )}
                    {folders.length === 0 && collaborativeMaps.length === 0 && !showNewFolder && <MyContentEmptyState type="folders" />}
                    {folders.map(folder => {
                      const itemsInFolder = content.filter(c => c.folderId === folder.id);
                      const packingInFolder = packingLists.filter(pl => pl.folderId === folder.id && !pl.archived);
                      const totalCount = itemsInFolder.length + packingInFolder.length;
                      const isExpanded = expandedFolderIds.has(folder.id);
                      const isEditing = editingFolderId === folder.id;
                      return (
                        <div key={folder.id}>
                          <div
                            onClick={() => { if (isEditing) return; setExpandedFolderIds(prev => {
                              const next = new Set(prev);
                              if (next.has(folder.id)) next.delete(folder.id); else next.add(folder.id);
                              return next;
                            }); }}
                            className={`flex items-center gap-3 px-4 py-3 rounded-2xl bg-[var(--bg-input)]/60 border border-[var(--border-primary)] transition-colors ${isEditing ? '' : 'hover:bg-[var(--bg-input)] cursor-pointer'}`}
                          >
                            <div className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ backgroundColor: folder.color + '20' }}>
                              <FolderOpen size={16} style={{ color: folder.color }} />
                            </div>
                            {isEditing ? (
                              <input
                                autoFocus
                                value={editingFolderName}
                                onChange={e => setEditingFolderName(e.target.value)}
                                onClick={e => e.stopPropagation()}
                                onKeyDown={e => {
                                  if (e.key === 'Enter') handleSaveRename();
                                  if (e.key === 'Escape') { setEditingFolderId(null); setEditingFolderName(''); }
                                }}
                                className="flex-1 min-w-0 bg-[var(--bg-input)] border border-[var(--border-primary)] rounded-lg px-2 py-1 text-[12px] font-bold text-[var(--text-primary)] focus:outline-none focus:ring-1 focus:ring-emerald-500"
                              />
                            ) : (
                              <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">{folder.name}</p>
                                <p className="text-2xs text-[var(--text-secondary)]">{t('myContent.items_count', { count: totalCount })}</p>
                              </div>
                            )}
                            {isEditing ? (
                              <>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleSaveRename(); }}
                                  className="p-2 rounded-xl text-emerald-400 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
                                  title={t('common.save', 'Lagre')}
                                >
                                  <Check size={14} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); setEditingFolderId(null); setEditingFolderName(''); }}
                                  className="p-2 rounded-xl text-[var(--text-muted)] hover:bg-[var(--bg-input)] transition-colors flex-shrink-0"
                                  title={t('common.cancel', 'Avbryt')}
                                >
                                  <X size={14} />
                                </button>
                              </>
                            ) : (
                              <>
                                {itemsInFolder.length > 0 && onFlyToContent && (
                                  <button
                                    onClick={(e) => { e.stopPropagation(); handleShowFolderOnMap(folder.id); }}
                                    className="p-2 rounded-xl text-[var(--text-muted)] hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors flex-shrink-0"
                                    title={t('myContent.show_on_map')}
                                  >
                                    <Locate size={14} />
                                  </button>
                                )}
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleStartRename(folder); }}
                                  className="p-2 rounded-xl text-[var(--text-muted)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-input)] transition-colors flex-shrink-0"
                                  title={t('myContent.rename_folder', 'Gi nytt navn')}
                                >
                                  <Pencil size={14} />
                                </button>
                                <button
                                  onClick={(e) => { e.stopPropagation(); handleDeleteFolder(folder); }}
                                  className="p-2 rounded-xl text-[var(--text-muted)] hover:text-red-400 hover:bg-red-500/10 transition-colors flex-shrink-0"
                                  title={t('myContent.delete_folder', 'Slett mappe')}
                                >
                                  <Trash2 size={14} />
                                </button>
                                {totalCount > 0 && (
                                  isExpanded
                                    ? <ChevronDown size={14} className="text-[var(--text-muted)] flex-shrink-0" />
                                    : <ChevronRight size={14} className="text-[var(--text-muted)] flex-shrink-0" />
                                )}
                              </>
                            )}
                          </div>
                          {isExpanded && totalCount > 0 && (
                            <div className="ml-6 mt-1 space-y-1">
                              {itemsInFolder.map(item => (
                                <MyContentItem
                                  key={item.id}
                                  item={item}
                                  isSelectMode={isSelectMode}
                                  isSelected={selectedIds.has(item.id)}
                                  onToggleSelect={handleToggleSelect}
                                  onTap={handleTapItem}
                                  onDelete={handleDeleteItem}
                                  onMoveToFolder={isPaidUser ? (id) => setShowMoveDialog(id) : undefined}
                                  onEdit={item.type === 'track' && onViewTrack ? (i) => onViewTrack(i as TrackContent) : undefined}
                                  spotPhotos={spotPhotos}
                                  reviewedSpotIds={reviewedSpotIds}
                                  isBusy={openingTrackId === item.id}
                                />
                              ))}
                              {packingInFolder.map(pl => {
                                const total = pl.categories.reduce((a, c) => a + c.items.length, 0);
                                const checked = pl.categories.reduce((a, c) => a + c.items.filter(i => i.checked).length, 0);
                                const pct = total > 0 ? Math.round((checked / total) * 100) : 0;
                                return (
                                  <button
                                    key={pl.id}
                                    onClick={() => { setSelectedPackingListId(pl.id); setActiveTab('packing'); }}
                                    className="w-full flex items-center gap-2.5 px-3 py-2 rounded-xl bg-[var(--bg-input)]/40 border border-[var(--border-primary)] hover:bg-[var(--bg-input)] transition-colors text-left"
                                  >
                                    <ClipboardList size={14} className="text-emerald-400 shrink-0" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-tiny font-bold text-[var(--text-primary)] truncate">{pl.name}</p>
                                      <div className="flex items-center gap-2 mt-0.5">
                                        <div className="flex-1 h-1 bg-[var(--bg-input)] rounded-full overflow-hidden">
                                          <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${pct}%` }} />
                                        </div>
                                        <span className="text-3xs font-bold text-[var(--text-muted)] shrink-0">{checked}/{total}</span>
                                      </div>
                                    </div>
                                  </button>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      );
                    })}

                    {/* Collaborative maps section */}
                    {collaborativeMaps.length > 0 && (
                      <>
                        <div className="border-t border-[var(--border-primary)] my-3" />
                        <p className="text-3xs font-bold text-[var(--text-muted)] uppercase tracking-wider px-1 py-1">
                          {t('myContent.collab_maps_section')}
                        </p>
                        {collaborativeMaps.map(m => {
                          const isOwner = currentUser ? m.createdBy === currentUser.uid : false;
                          const memberCount = m.members.length;
                          // «Nytt»: et annet medlem har lagt til innhold eller sendt chat siden
                          // sist. Innhold markeres som sett når DENNE mappa åpnes (ikke ved
                          // panel-åpning) — så brukeren ser hvilken mappe det gjelder.
                          const hasNew = currentUser
                            ? hasUnseenCollabContent(m, currentUser.uid) || hasUnreadCollabChat(m, currentUser.uid)
                            : false;
                          const openMap = () => {
                            if (currentUser && hasUnseenCollabContent(m, currentUser.uid)) {
                              void markCollabContentSeen(m.id, currentUser.uid).catch(() => { /* stille */ });
                            }
                            if (allSpots) setInlineCollabMapId(m.id); else onOpenCollabMap?.(m.id);
                          };
                          return (
                            <div
                              key={m.id}
                              onClick={openMap}
                              className={`w-full flex items-center gap-3 px-4 py-3 rounded-2xl bg-[var(--bg-input)]/60 border transition-colors cursor-pointer text-left hover:bg-[var(--bg-input)] ${hasNew ? 'border-sky-400/60 ring-1 ring-sky-400/30' : 'border-[var(--border-primary)]'}`}
                              role="button"
                              tabIndex={0}
                              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') openMap(); }}
                            >
                              <div className="relative w-9 h-9 rounded-xl bg-sky-500/20 flex items-center justify-center flex-shrink-0">
                                <MapIcon size={16} className="text-sky-400" />
                                {hasNew && <span className="absolute -top-1 -right-1 h-2.5 w-2.5 rounded-full bg-sky-400 border-2 border-[var(--bg-surface)]" />}
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-[12px] font-bold text-[var(--text-primary)] truncate">
                                  {m.name}
                                  {hasNew && <span className="ml-1.5 rounded-pill bg-sky-500/20 px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider text-sky-400 align-middle">{t('myContent.collab_new', 'Nytt')}</span>}
                                </p>
                                <p className="text-2xs text-[var(--text-secondary)]">
                                  {m.spotIds.length} {m.spotIds.length === 1 ? 'plass' : 'plasser'}
                                  {(m.routeIds?.length || 0) > 0 && ` · ${m.routeIds.length} ${m.routeIds.length === 1 ? 'rute' : 'ruter'}`}
                                  {' · '}<Users size={9} className="inline" /> {memberCount}
                                </p>
                              </div>
                              <span className="text-3xs text-[var(--text-muted)] flex-shrink-0">
                                {isOwner ? t('myContent.collab_owner') : t('myContent.collab_editor')}
                              </span>
                              {onShowCollabMapOnMap && (m.spotIds.length > 0 || (m.routeIds?.length || 0) > 0) && (
                                <button
                                  onClick={(e) => { e.stopPropagation(); onShowCollabMapOnMap(m.id); }}
                                  className="p-2 rounded-xl text-[var(--text-muted)] hover:text-sky-400 hover:bg-sky-500/10 transition-colors flex-shrink-0"
                                  title={t('myContent.show_on_map')}
                                >
                                  <Locate size={14} />
                                </button>
                              )}
                            </div>
                          );
                        })}
                      </>
                    )}

                    {/* Create new collaborative map */}
                    {onCreateCollabMap && (
                      <button
                        onClick={onCreateCollabMap}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl border border-dashed border-sky-500/30 text-sky-400 hover:bg-sky-500/10 transition-colors text-tiny font-bold"
                      >
                        <Plus size={14} /> {t('myContent.new_collab_map')}
                      </button>
                    )}
                  </>
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 gap-3">
                    <Lock size={24} className="text-[var(--text-muted)]" />
                    <p className="text-[12px] font-bold text-[var(--text-secondary)]">{t('myContent.folders_require_premium')}</p>
                    <button
                      onClick={() => onShowNudge?.('content_folders')}
                      className="px-4 py-2 bg-emerald-500 text-white rounded-xl text-tiny font-bold"
                    >
                      {t('myContent.upgrade')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                {filteredContent.length === 0 ? (
                  <MyContentEmptyState type={activeTab as ContentType} />
                ) : (
                  filteredContent.map(item => (
                    <MyContentItem
                      key={item.id}
                      item={item}
                      isSelectMode={isSelectMode}
                      isSelected={selectedIds.has(item.id)}
                      onToggleSelect={handleToggleSelect}
                      onTap={handleTapItem}
                      onDelete={handleDeleteItem}
                      onMoveToFolder={isPaidUser ? (id) => setShowMoveDialog(id) : undefined}
                      onEdit={item.type === 'track' && onViewTrack ? (i) => onViewTrack(i as TrackContent) : undefined}
                      spotPhotos={spotPhotos}
                      isBusy={openingTrackId === item.id}
                    />
                  ))
                )}
              </>
            )}
          </div>

          {/* Bulk action bar */}
          {isSelectMode && selectedIds.size > 0 && (
            <div className="flex items-center gap-2 px-5 py-3 border-t border-[var(--border-primary)] bg-[var(--bg-surface)] flex-shrink-0">
              <span className="text-2xs font-bold text-[var(--text-secondary)] flex-1">{t('myContent.selected_count', { count: selectedIds.size })}</span>
              <button
                onClick={handleExportSelected}
                className="flex items-center gap-1 px-3 py-1.5 bg-[var(--bg-input)] text-[var(--text-secondary)] rounded-lg text-2xs font-bold hover:bg-[var(--bg-input)]"
              >
                <Download size={12} /> GPX
              </button>
              {isPaidUser && (
                <button
                  onClick={() => setShowMoveDialog('bulk')}
                  className="flex items-center gap-1 px-3 py-1.5 bg-[var(--bg-input)] text-[var(--text-secondary)] rounded-lg text-2xs font-bold hover:bg-[var(--bg-input)]"
                >
                  <FolderInput size={12} /> {t('myContent.move')}
                </button>
              )}
              <button
                onClick={handleBulkDelete}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-500/20 text-red-400 rounded-lg text-2xs font-bold hover:bg-red-500/30"
              >
                <Trash2 size={12} /> {t('myContent.delete')}
              </button>
            </div>
          )}
        </>
      )}

      {/* Import GPX dialog */}
      {showImport && userId && (
        <MyContentImportDialog
          userId={userId}
          onClose={() => setShowImport(false)}
          onImported={() => setShowImport(false)}
        />
      )}

      {/* Move to folder dialog */}
      {showMoveDialog && (
        <div className="fixed inset-0 z-[2100] flex items-end sm:items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setShowMoveDialog(null)} />
          <div className="relative w-full sm:max-w-sm bg-[var(--bg-surface)] rounded-t-2xl sm:rounded-2xl border border-[var(--border-secondary)] shadow-xl max-h-[50dvh] flex flex-col overflow-hidden mx-0 sm:mx-4" style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}>
            <div className="flex items-center justify-between px-4 pt-4 pb-2">
              <h3 className="text-[12px] font-bold text-[var(--text-primary)]">{t('myContent.move_to_folder')}</h3>
              <button onClick={() => setShowMoveDialog(null)} className="p-1 text-[var(--text-secondary)] hover:text-[var(--text-primary)]">
                <X size={14} />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-1">
              <button
                onClick={() => showMoveDialog === 'bulk' ? handleBulkMove(null) : runMoveToFolder([showMoveDialog], null)}
                className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-tiny text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]"
              >
                <Layers size={14} /> {t('myContent.root_level')}
              </button>
              {folders.map(f => (
                <button
                  key={f.id}
                  onClick={() => showMoveDialog === 'bulk' ? handleBulkMove(f.id) : runMoveToFolder([showMoveDialog], f.id)}
                  className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-tiny text-[var(--text-secondary)] hover:bg-[var(--bg-surface-alt)]"
                >
                  <FolderOpen size={14} style={{ color: f.color }} /> {f.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyContentInner;
